/**
 * What a backup actually did, guest by guest.
 *
 * Clicking a bar on the timeline asks this: the bar's colour says the job had
 * trouble, and the only question that follows is *which guest*. So the failed
 * ones come first and keep their reason, the rest are a quiet list, and the
 * per-guest progress chatter that makes up nine tenths of a real task log is
 * left on the PVE node where it belongs.
 *
 * The report is fetched per click rather than polled: PVE only states any of
 * this in the task's log text, and a finished task's log never changes.
 */

import { useEffect, useState } from 'react';
import type { BackupGuestReport, BackupReport, BackupRun } from '@proxmon/shared';
import { api, ApiError } from '../api/client.ts';
import { groupGuests } from './backups.ts';
import { clockTime, dayTime, elapsed } from '../format.ts';
import { useStore } from '../state/store.ts';

export function BackupReportDialog({ run, onClose }: { run: BackupRun; onClose: () => void }) {
  const [report, setReport] = useState<BackupReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let live = true;
    setReport(null);
    setError(null);

    void (async () => {
      try {
        const path = `/api/backups/${encodeURIComponent(run.node)}/${encodeURIComponent(run.upid)}`;
        const fetched = await api.get<BackupReport>(path);
        if (live) setReport(fetched);
      } catch (err) {
        // A task log Proxmox has rotated away is the common one, and it is not
        // a fault worth a toast — the dialog says so where it was expected.
        if (live) setError(err instanceof ApiError || err instanceof Error ? err.message : 'Could not read the log.');
      }
    })();

    return () => {
      live = false;
    };
    // `endedAt` is in the deps so a backup that finishes while its report is
    // open is re-read once, rather than leaving the half-told story on screen.
  }, [run.node, run.upid, run.endedAt]);

  return (
    <div className="overlay" onPointerDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog wide backup-report" role="dialog" aria-modal="true" aria-label="Backup report">
        <header>
          <div>
            <h2>{title(run)}</h2>
            <div className="sub">{when(run)}</div>
          </div>
          <button type="button" className="close" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="body">
          {error ? (
            <p className="empty">{error}</p>
          ) : !report ? (
            <p className="empty">Reading the task log on {run.node}…</p>
          ) : (
            <Report report={report} />
          )}
        </div>
      </div>
    </div>
  );
}

function Report({ report }: { report: BackupReport }) {
  const guests = useStore((s) => s.snapshot?.guests);

  if (report.guests.length === 0) {
    return (
      <>
        <Job report={report} />
        <p className="empty">
          {report.running
            ? 'The job has not reached its first guest yet.'
            : 'This task log names no guests. Proxmox may have trimmed it, or the job never got as far as one.'}
        </p>
      </>
    );
  }

  const groups = groupGuests(report.guests);

  return (
    <>
      <div className="tally">
        {groups.map((group) => (
          <span key={group.outcome} className={`pill ${group.tone}`}>
            {group.tally}
          </span>
        ))}
      </div>

      {groups.map((group) => (
        <section key={group.outcome}>
          <h3>
            {group.title} <span className="count">{group.rows.length}</span>
          </h3>
          <ul className="guest-rows">
            {group.rows.map((guest) => (
              <GuestRow
                key={guest.vmid}
                guest={guest}
                tone={group.tone}
                // The name vzdump logged is what the archive was called at the
                // time; the snapshot only fills in for a log that never said.
                liveName={guests?.find((candidate) => candidate.vmid === guest.vmid)?.name ?? null}
              />
            ))}
          </ul>
        </section>
      ))}

      <Job report={report} />
    </>
  );
}

function GuestRow({
  guest,
  tone,
  liveName,
}: {
  guest: BackupGuestReport;
  tone: string;
  liveName: string | null;
}) {
  const name = guest.name ?? liveName;

  return (
    <li className={`guest-row ${tone}`}>
      <div className="who">
        <span className="vmid">{guest.vmid}</span>
        <span className="name">{name ?? 'unnamed guest'}</span>
        {guest.kind && <span className="kind">{guest.kind}</span>}
      </div>
      <div className="facts">
        {guest.durationSeconds !== null && <span>{elapsed(guest.durationSeconds * 1000)}</span>}
        {guest.archiveSize && <span>{guest.archiveSize}</span>}
      </div>
      {guest.error && <p className="reason">{guest.error}</p>}
      {guest.warnings.map((warning, index) => (
        <p className="reason warn" key={index}>
          {warning}
        </p>
      ))}
    </li>
  );
}

/** What the job as a whole did: where it wrote, and how PVE signed it off. */
function Job({ report }: { report: BackupReport }) {
  return (
    <>
      {report.jobErrors.length > 0 && (
        <section>
          <h3>The job itself</h3>
          {report.jobErrors.map((line, index) => (
            <p className="reason" key={index}>
              {line}
            </p>
          ))}
        </section>
      )}

      <dl className="kv">
        <dt>Storage</dt>
        <dd>{report.storage ?? 'not named in the log'}</dd>
        <dt>Proxmox says</dt>
        <dd>{report.status ?? (report.running ? 'still running' : '—')}</dd>
      </dl>

      {report.truncated && (
        <p className="empty">
          This log is longer than Proxmon will read ({report.lines.toLocaleString()} lines), so guests backed up
          towards the end of the job may be missing from this report.
        </p>
      )}
    </>
  );
}

function title(run: BackupRun): string {
  if (run.vmid === null) return `Backup job on ${run.node}`;
  return `Backup of ${run.guestName ?? 'guest'} (${run.vmid})`;
}

function when(run: BackupRun): string {
  if (run.endedAt === null) {
    return `${run.node} · started ${dayTime(run.startedAt)}, running for ${elapsed(Date.now() - run.startedAt)}`;
  }
  return `${run.node} · ${dayTime(run.startedAt)} → ${clockTime(run.endedAt)} · ${elapsed(run.endedAt - run.startedAt)}`;
}
